/** Requête d'envoi d'un batch, indépendante de l'API réseau utilisée. */
export type TransportRequest = {
  /** URL de l'endpoint, avec `?compression=gzip` si le corps est compressé. */
  url: string
  /** Batch sérialisé en JSON (UTF-8), compressé ou non selon l'URL. */
  body: Uint8Array
  /** Envoi qui doit survivre à la fermeture de l'onglet. */
  keepalive: boolean
}

export type TransportResponse = {
  status: number
  /** En-tête `Retry-After` (429), en secondes. */
  retryAfterSec?: number
  /** Code d'erreur renvoyé par l'API (`{ error }`), par exemple `session_too_large`. */
  error?: string
  /** Corps JSON d'une réponse réussie, s'il y en a un (la réponse d'une Exception). */
  body?: unknown
}

/** Envoie une requête et renvoie sa réponse ; rejette sur erreur réseau. Injectable pour les tests. */
export type Transport = (request: TransportRequest) => Promise<TransportResponse>

/**
 * Transport par défaut : `fetch` en `text/plain`, une requête « simple » au sens CORS qui
 * n'exige pas de requête préalable (l'API accepte ce type pour l'ingestion).
 */
export const fetchTransport: Transport = async (request) => {
  const res = await fetch(request.url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: request.body as Uint8Array<ArrayBuffer>,
    keepalive: request.keepalive,
    credentials: 'omit',
  })
  const response: TransportResponse = { status: res.status }
  const retryAfterSec = retryAfterSeconds(res.headers.get('Retry-After'))
  if (retryAfterSec !== undefined) response.retryAfterSec = retryAfterSec
  // Corps toujours lu (vide en cas de succès) : la connexion peut resservir au batch suivant.
  const text = await res.text().catch(() => '')
  if (res.status >= 400) {
    const error = parseError(text)
    if (error !== undefined) response.error = error
  } else if (text !== '') {
    try {
      response.body = JSON.parse(text) as unknown
    } catch {
      // Corps illisible : ignoré.
    }
  }
  return response
}

/** Code d'erreur d'un corps `{ "error": "…" }` renvoyé par l'API. */
function parseError(text: string): string | undefined {
  try {
    const body = JSON.parse(text) as { error?: unknown } | null
    return typeof body?.error === 'string' ? body.error : undefined
  } catch {
    return undefined
  }
}

/** `Retry-After` en secondes ou en date HTTP (cf. RFC 9110) ; `undefined` si absent ou illisible. */
function retryAfterSeconds(header: string | null): number | undefined {
  if (header === null || header.trim() === '') return undefined
  const seconds = Number(header)
  if (Number.isFinite(seconds)) return Math.max(0, seconds)
  const date = Date.parse(header)
  return Number.isNaN(date) ? undefined : Math.max(0, (date - Date.now()) / 1_000)
}
